/* Pure plan generator. Does not fetch, mutate a cart, reserve stock, or authorize checkout. */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./swell-pricing-core.js'));
  } else {
    root.SwellGiftCartPolicy = factory(root.SwellPricingCore);
  }
}(typeof globalThis !== 'undefined' ? globalThis : this, function (pricingCore) {
  'use strict';
  if (!pricingCore || typeof pricingCore.createPricing !== 'function') {
    throw new Error('Load SwellPricingCore before SwellGiftCartPolicy');
  }
  var GIFT_VARIANT_ID = '67679733973305';
  var GIFT_SKU = 'SWELL-HOLIDAY-BAG-2026';

  function object(value, name) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new TypeError(name + ' must be an object');
    return value;
  }
  function integer(value, name) {
    if (!Number.isSafeInteger(value) || value < 0) throw new TypeError(name + ' must be a nonnegative safe integer');
    return value;
  }
  function sum(values, name) {
    var total = values.reduce(function (result, value) { return result + BigInt(value); }, BigInt(0));
    if (total > BigInt(Number.MAX_SAFE_INTEGER)) throw new RangeError(name + ' exceeds the safe integer range');
    return Number(total);
  }
  function id(value, type, optional) {
    if (optional && (value === undefined || value === null)) return null;
    if (typeof value === 'number') {
      if (!Number.isSafeInteger(value) || value <= 0) throw new TypeError('Unsafe ' + type + ' ID');
      value = String(value);
    }
    if (typeof value !== 'string') throw new TypeError('Invalid ' + type + ' ID');
    var prefix = 'gid://shopify/' + type + '/';
    if (value.indexOf(prefix) === 0) value = value.slice(prefix.length);
    if (!/^[1-9][0-9]*$/.test(value)) throw new TypeError('Invalid ' + type + ' ID');
    return value;
  }
  function stable(value) {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') return JSON.stringify(value);
    if (typeof value === 'number' && Number.isFinite(value)) return JSON.stringify(value);
    if (Array.isArray(value)) return '[' + Array.from(value, stable).join(',') + ']';
    if (value && typeof value === 'object') {
      return '{' + Object.keys(value).sort().map(function (key) {
        return JSON.stringify(key) + ':' + stable(value[key]);
      }).join(',') + '}';
    }
    throw new TypeError('Snapshot properties must contain JSON values');
  }
  // Deterministic, non-security fingerprint: a stale-plan guard, not authentication.
  function fingerprint(value) {
    var input = stable(value);
    var hash = BigInt('14695981039346656037');
    for (var i = 0; i < input.length; i += 1) {
      hash ^= BigInt(input.charCodeAt(i));
      hash = BigInt.asUintN(64, hash * BigInt('1099511628211'));
    }
    return hash.toString(16).padStart(16, '0');
  }
  function planIdOf(line, native) {
    if (!native) return id(line.sellingPlanId, 'SellingPlan', true);
    var allocated = line.selling_plan_allocation;
    var allocationId = allocated ? id(allocated.selling_plan && allocated.selling_plan.id, 'SellingPlan', false) : null;
    var directId = line.selling_plan === undefined || line.selling_plan === null
      ? null : id(line.selling_plan, 'SellingPlan', false);
    if (allocationId && directId && allocationId !== directId) throw new TypeError('Conflicting selling plan IDs');
    return allocationId || directId;
  }
  function normalizedLines(snapshot) {
    object(snapshot, 'snapshot');
    var native = snapshot.items !== undefined;
    if (native && snapshot.lines !== undefined) throw new TypeError('Use either snapshot.items or snapshot.lines');
    var source = native ? snapshot.items : snapshot.lines;
    if (!Array.isArray(source)) throw new TypeError('snapshot must contain an items or lines array');
    var keys = new Set();
    return Array.from(source, function (line, index) {
      object(line, 'line ' + index);
      if (typeof line.key !== 'string' || !line.key) throw new TypeError('Every line needs its current nonempty cart key');
      if (keys.has(line.key)) throw new TypeError('Duplicate cart line key');
      keys.add(line.key);
      if (line.isExistingSubscription !== undefined && typeof line.isExistingSubscription !== 'boolean') {
        throw new TypeError('isExistingSubscription must be boolean');
      }
      return {
        key: line.key,
        variantId: id(native ? line.variant_id : line.variantId, 'ProductVariant', false),
        productId: id(native ? line.product_id : line.productId, 'Product', true),
        sellingPlanId: planIdOf(line, native),
        quantity: integer(line.quantity, 'line quantity'),
        properties: line.properties === undefined || line.properties === null ? {} : object(line.properties, 'line properties'),
        isExistingSubscription: line.isExistingSubscription === true
      };
    });
  }

  function planGiftCart(snapshot, config) {
    config = config === undefined ? {} : object(config, 'config');
    var approved = config.allowedNewPlanIds === undefined ? [] : config.allowedNewPlanIds;
    // Pricing core enforces exact product/variant/plan exclusions, including hard trial exclusions.
    var calculator = pricingCore.createPricing({ approvedSellingPlanIds: approved, giftVariantIds: [GIFT_VARIANT_ID] });
    if (config.optOut !== undefined && typeof config.optOut !== 'boolean') throw new TypeError('optOut must be boolean');
    var optOut = config.optOut === true;
    var knownStock = config.giftStock !== undefined && config.giftStock !== null;
    var giftStock = knownStock ? integer(config.giftStock, 'giftStock') : null;
    var lines = normalizedLines(snapshot);
    if (snapshot.isComplete !== undefined && typeof snapshot.isComplete !== 'boolean') throw new TypeError('isComplete must be boolean');
    var priceResult = calculator.evaluate(lines.map(function (line) {
      return Object.assign({}, line, { regularUnitPriceCents: 0 });
    }));
    var giftLines = lines.filter(function (line) { return line.variantId === GIFT_VARIANT_ID && line.quantity > 0; });
    var merchandiseQuantity = sum(lines.filter(function (line) { return line.variantId !== GIFT_VARIANT_ID; }).map(function (line) { return line.quantity; }), 'merchandise quantity');
    var normalizedForFingerprint = lines.slice().sort(function (a, b) { return a.key.localeCompare(b.key); });
    var snapshotFingerprint = fingerprint(normalizedForFingerprint);
    var manual = config.selectedGiftQuantity != null ? integer(config.selectedGiftQuantity, 'selectedGiftQuantity') : null;
    var requested = optOut ? 0 : manual !== null ? manual : priceResult.giftQuantity;
    if(config.paidGiftExtras!==true)requested=Math.min(requested,priceResult.giftQuantity);
    var desiredQuantity = requested === 0 ? 0 : knownStock ? Math.min(requested, giftStock) : null;
    var result = {
      version: '1.0.0-proposal',
      giftVariantId: GIFT_VARIANT_ID,
      giftSku: GIFT_SKU,
      snapshotFingerprint: snapshotFingerprint,
      planId: null,
      status: 'NO_CHANGE',
      deferredReason: null,
      eligibleFullSizeQuantity: priceResult.eligibleQuantity,
      merchandiseQuantity: merchandiseQuantity,
      shippingRewardsQuantity: merchandiseQuantity,
      entitledGiftQuantity: priceResult.giftQuantity,
      currentGiftQuantity: sum(giftLines.map(function (line) { return line.quantity; }), 'gift quantity'),
      desiredGiftQuantity: desiredQuantity,
      confirmedGiftQuantity: 0,
      paidGiftQuantity: desiredQuantity === null ? null : Math.max(0, desiredQuantity - priceResult.giftQuantity),
      giftStock: giftStock,
      optOut: optOut,
      actions: [],
      nonGiftLinesUntouched: true,
      requiresFreshSnapshot: false,
      nativeCheckoutValidation: 'REQUIRED_ZERO_PRICE_GIFT_IS_NOT_PROTECTED_BY_THIS_PLANNER'
    };
    var pending = config.pendingMutation;
    if (pending !== undefined && pending !== null) {
      object(pending, 'pendingMutation');
      if (typeof pending.planId !== 'string' || !pending.planId || typeof pending.snapshotFingerprint !== 'string' || !pending.snapshotFingerprint) {
        throw new TypeError('pendingMutation needs planId and snapshotFingerprint');
      }
      if (pending.status !== 'in_flight' && pending.status !== 'awaiting_fresh_snapshot') throw new TypeError('Invalid pendingMutation status');
    }
    if (snapshot.isComplete === false) result.deferredReason = 'FULL_CART_SNAPSHOT_REQUIRED';
    else if (pending && pending.status === 'in_flight') result.deferredReason = 'MUTATION_IN_FLIGHT';
    else if (pending && pending.status === 'awaiting_fresh_snapshot' && pending.snapshotFingerprint === snapshotFingerprint) result.deferredReason = 'FRESH_SNAPSHOT_REQUIRED';
    else if (desiredQuantity === null) result.deferredReason = 'GIFT_STOCK_UNKNOWN';
    if (result.deferredReason) {
      result.status = 'DEFERRED';
      result.requiresFreshSnapshot = result.deferredReason !== 'GIFT_STOCK_UNKNOWN';
      return result;
    }

    var oneTimeLines = giftLines.filter(function (line) { return line.sellingPlanId === null; });
    var keep = desiredQuantity > 0
      ? oneTimeLines.find(function (line) { return line.quantity === desiredQuantity; }) || oneTimeLines[0]
      : null;
    giftLines.forEach(function (line) {
      if (line === keep) return;
      result.actions.push({
        type: 'change', key: line.key, quantity: 0,
        reason: line.sellingPlanId !== null ? 'GIFT_MUST_BE_ONE_TIME'
          : optOut ? 'CUSTOMER_DECLINED_GIFT'
          : desiredQuantity === 0 ? 'NO_CURRENT_GIFT_ENTITLEMENT'
          : 'MERGE_DUPLICATE_GIFT_LINES'
      });
    });
    if (keep && keep.quantity !== desiredQuantity) {
      result.actions.push({ type: 'change', key: keep.key, quantity: desiredQuantity, reason: 'MATCH_VERIFIED_GIFT_ENTITLEMENT' });
    } else if (!keep && desiredQuantity > 0) {
      // Intentionally no selling_plan / sellingPlanId or inherited product properties.
      result.actions.push({ type: 'add', variantId: GIFT_VARIANT_ID, quantity: desiredQuantity, reason: 'ADD_ONE_TIME_GIFT' });
    }
    result.status = result.actions.length ? 'CHANGE_REQUIRED' : 'NO_CHANGE';
    result.requiresFreshSnapshot = result.actions.length > 0;
    // Claim only a verified, already-correct one-time bag quantity. A proposed add is not a gift confirmation.
    result.confirmedGiftQuantity = result.actions.length === 0 && keep ? Math.min(keep.quantity, priceResult.giftQuantity) : 0;
    result.planId = fingerprint({
      snapshotFingerprint: snapshotFingerprint,
      allowedNewPlanIds: Array.from(new Set(approved.map(function (value) { return id(value, 'SellingPlan', false); }))).sort(),
      giftStock: giftStock, optOut: optOut, manual: manual, actions: result.actions
    });
    return result;
  }

  function giftPriceEvidence(cart, entitlement, paidExtras) {
    var remaining=entitlement;
    return cart.items.filter(function(l){return String(l.variant_id)===GIFT_VARIANT_ID;}).every(function(l){
      if(l.selling_plan_allocation||l.selling_plan)return false;
      if(!paidExtras)return l.final_line_price===0;
      var free=Math.min(l.quantity,remaining);remaining-=free;
      var price=l.original_price;
      return Number.isSafeInteger(price)&&price>0&&Number.isSafeInteger(l.final_line_price)&&l.final_line_price>=0&&l.final_line_price<=(l.quantity-free)*price;
    });
  }
  return Object.freeze({ giftPriceEvidence:giftPriceEvidence, giftVariantId: GIFT_VARIANT_ID, giftSku: GIFT_SKU, planGiftCart: planGiftCart });
}));

